import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { apiFetch } from "@/lib/api-fetch";
import {
  Plus,
  Search,
  Trash2,
  Eye,
  AlertTriangle,
  ChevronDown,
  X,
  Shield,
  Clock,
  ArrowLeft,
  CheckCircle2,
} from "lucide-react";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type Incident = {
  id: number;
  incident_number: string;
  title: string;
  description: string | null;
  incident_type: string;
  severity: string;
  status: string;
  location: string | null;
  date_occurred: string | null;
  date_reported: string;
  reported_by: string | null;
  reported_by_email: string | null;
  assigned_to: string | null;
  witnesses: string | null;
  injuries_reported: boolean;
  injury_details: string | null;
  equipment_involved: string | null;
  root_cause: string | null;
  corrective_actions: string | null;
  preventive_actions: string | null;
  resolution_notes: string | null;
  resolved_at: string | null;
  photos: string[] | null;
  client_id: number | null;
  work_order_id: number | null;
  organization_id: number | null;
};

type StatusFilter = "all" | "open" | "investigating" | "resolved" | "closed";
type SeverityFilter = "all" | "low" | "medium" | "high" | "critical";

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

const STATUS_CONFIG: Record<string, { label: string; bg: string; text: string }> = {
  open: { label: "Open", bg: "bg-red-100 dark:bg-red-900/30", text: "text-red-700 dark:text-red-400" },
  investigating: { label: "Investigating", bg: "bg-yellow-100 dark:bg-yellow-900/30", text: "text-yellow-700 dark:text-yellow-400" },
  resolved: { label: "Resolved", bg: "bg-green-100 dark:bg-green-900/30", text: "text-green-700 dark:text-green-400" },
  closed: { label: "Closed", bg: "bg-gray-100 dark:bg-gray-800", text: "text-gray-600 dark:text-gray-400" },
};

const SEVERITY_CONFIG: Record<string, { label: string; bg: string; text: string; dot: string }> = {
  low: { label: "Low", bg: "bg-green-100 dark:bg-green-900/30", text: "text-green-700 dark:text-green-400", dot: "bg-green-500" },
  medium: { label: "Medium", bg: "bg-yellow-100 dark:bg-yellow-900/30", text: "text-yellow-700 dark:text-yellow-400", dot: "bg-yellow-500" },
  high: { label: "High", bg: "bg-orange-100 dark:bg-orange-900/30", text: "text-orange-700 dark:text-orange-400", dot: "bg-orange-500" },
  critical: { label: "Critical", bg: "bg-red-100 dark:bg-red-900/30", text: "text-red-700 dark:text-red-400", dot: "bg-red-500" },
};

const INCIDENT_TYPES = [
  { value: "injury", label: "Injury" },
  { value: "equipment_failure", label: "Equipment Failure" },
  { value: "spill", label: "Spill" },
  { value: "near_miss", label: "Near Miss" },
  { value: "property_damage", label: "Property Damage" },
  { value: "other", label: "Other" },
];

function formatTypeLabel(type: string) {
  const found = INCIDENT_TYPES.find((t) => t.value === type);
  if (found) return found.label;
  return type.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatDate(dateStr: string | null | undefined) {
  if (!dateStr) return "--";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return "--";
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function formatDateTime(dateStr: string | null | undefined) {
  if (!dateStr) return "--";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return "--";
  return d.toLocaleString("en-US", {
    month: "short", day: "numeric", year: "numeric",
    hour: "numeric", minute: "2-digit",
  });
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function IncidentReports() {
  const { token } = useAuth();

  const [incidents, setIncidents] = useState<Incident[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [severityFilter, setSeverityFilter] = useState<SeverityFilter>("all");
  const [search, setSearch] = useState("");

  // Bulk selection
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState<number | null>(null);

  // Create modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);

  // Detail panel
  const [selectedIncident, setSelectedIncident] = useState<Incident | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  // Form state
  const emptyForm = {
    title: "",
    description: "",
    incident_type: "other",
    severity: "medium",
    location: "",
    date_occurred: new Date().toISOString().split("T")[0],
    witnesses: "",
    injuries_reported: false,
    injury_details: "",
    equipment_involved: "",
    assigned_to: "",
    root_cause: "",
    corrective_actions: "",
    preventive_actions: "",
    resolution_notes: "",
    status: "open",
  };
  const [form, setForm] = useState(emptyForm);

  // -----------------------------------------------------------------------
  // Data fetching
  // -----------------------------------------------------------------------

  const fetchIncidents = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== "all") params.set("status", statusFilter);
      if (severityFilter !== "all") params.set("severity", severityFilter);
      const qs = params.toString();
      const url = `/api/ops/incidents${qs ? `?${qs}` : ""}`;
      const res = await apiFetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Failed to load incidents (${res.status})`);
      const data = await res.json();
      setIncidents(data.incidents || []);
    } catch (err: any) {
      setError(err?.message || "Failed to load incidents");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchIncidents();
  }, [token, statusFilter, severityFilter]);

  // -----------------------------------------------------------------------
  // Filtered
  // -----------------------------------------------------------------------

  const filtered = useMemo(() => {
    if (!search.trim()) return incidents;
    const q = search.toLowerCase();
    return incidents.filter(
      (inc) =>
        inc.incident_number.toLowerCase().includes(q) ||
        inc.title.toLowerCase().includes(q) ||
        (inc.reported_by && inc.reported_by.toLowerCase().includes(q)) ||
        (inc.location && inc.location.toLowerCase().includes(q))
    );
  }, [incidents, search]);

  const stats = useMemo(() => {
    const open = incidents.filter((i) => i.status === "open").length;
    const investigating = incidents.filter((i) => i.status === "investigating").length;
    const critical = incidents.filter((i) => i.severity === "critical" && i.status !== "closed" && i.status !== "resolved").length;
    return { total: incidents.length, open, investigating, critical };
  }, [incidents]);

  // -----------------------------------------------------------------------
  // Actions
  // -----------------------------------------------------------------------

  const toggleSelectAll = () => {
    if (selected.size === filtered.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(filtered.map((i) => i.id)));
    }
  };

  const toggleSelect = (id: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleBulkDelete = async () => {
    if (selected.size === 0) return;
    setDeleting(true);
    try {
      const res = await apiFetch("/api/ops/incidents/delete", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ids: Array.from(selected) }),
      });
      if (!res.ok) throw new Error("Delete failed");
      setSelected(new Set());
      setShowDeleteConfirm(false);
      await fetchIncidents();
    } catch (err: any) {
      setError(err?.message || "Failed to delete incidents");
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteSingle = async () => {
    if (!pendingDeleteId) return;
    setDeleting(true);
    try {
      const res = await apiFetch("/api/ops/incidents/delete", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ids: [pendingDeleteId] }),
      });
      if (!res.ok) throw new Error("Delete failed");
      setPendingDeleteId(null);
      if (selectedIncident?.id === pendingDeleteId) setSelectedIncident(null);
      await fetchIncidents();
    } catch (err: any) {
      setError(err?.message || "Failed to delete incident");
    } finally {
      setDeleting(false);
    }
  };

  const handleCreate = async () => {
    if (!form.title.trim()) return;
    setCreating(true);
    try {
      const res = await apiFetch("/api/ops/incidents", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          title: form.title.trim(),
          description: form.description || null,
          incident_type: form.incident_type,
          severity: form.severity,
          location: form.location || null,
          date_occurred: form.date_occurred ? new Date(form.date_occurred).toISOString() : null,
          witnesses: form.witnesses || null,
          injuries_reported: form.injuries_reported,
          injury_details: form.injuries_reported ? form.injury_details || null : null,
          equipment_involved: form.equipment_involved || null,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || "Failed to create incident");
      }
      setShowCreateModal(false);
      setForm(emptyForm);
      await fetchIncidents();
    } catch (err: any) {
      setError(err?.message || "Failed to create incident");
    } finally {
      setCreating(false);
    }
  };

  const openDetail = async (id: number) => {
    setDetailLoading(true);
    setEditing(false);
    try {
      const res = await apiFetch(`/api/ops/incidents/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Failed to load incident");
      const data = await res.json();
      setSelectedIncident(data);
      setForm({
        title: data.title || "",
        description: data.description || "",
        incident_type: data.incident_type || "other",
        severity: data.severity || "medium",
        location: data.location || "",
        date_occurred: data.date_occurred ? new Date(data.date_occurred).toISOString().split("T")[0] : "",
        witnesses: data.witnesses || "",
        injuries_reported: data.injuries_reported || false,
        injury_details: data.injury_details || "",
        equipment_involved: data.equipment_involved || "",
        assigned_to: data.assigned_to || "",
        root_cause: data.root_cause || "",
        corrective_actions: data.corrective_actions || "",
        preventive_actions: data.preventive_actions || "",
        resolution_notes: data.resolution_notes || "",
        status: data.status || "open",
      });
    } catch (err: any) {
      setError(err?.message || "Failed to load incident");
    } finally {
      setDetailLoading(false);
    }
  };

  const handleUpdate = async () => {
    if (!selectedIncident) return;
    setSaving(true);
    try {
      const res = await apiFetch(`/api/ops/incidents/${selectedIncident.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          title: form.title.trim(),
          description: form.description || null,
          incident_type: form.incident_type,
          severity: form.severity,
          status: form.status,
          location: form.location || null,
          date_occurred: form.date_occurred ? new Date(form.date_occurred).toISOString() : null,
          witnesses: form.witnesses || null,
          injuries_reported: form.injuries_reported,
          injury_details: form.injuries_reported ? form.injury_details || null : null,
          equipment_involved: form.equipment_involved || null,
          assigned_to: form.assigned_to || null,
          root_cause: form.root_cause || null,
          corrective_actions: form.corrective_actions || null,
          preventive_actions: form.preventive_actions || null,
          resolution_notes: form.resolution_notes || null,
        }),
      });
      if (!res.ok) throw new Error("Failed to update incident");
      const updated = await res.json();
      setSelectedIncident(updated);
      setEditing(false);
      await fetchIncidents();
    } catch (err: any) {
      setError(err?.message || "Failed to update incident");
    } finally {
      setSaving(false);
    }
  };

  // -----------------------------------------------------------------------
  // Detail view
  // -----------------------------------------------------------------------

  if (selectedIncident && !detailLoading) {
    const inc = selectedIncident;
    const statusCfg = STATUS_CONFIG[inc.status] || STATUS_CONFIG.open;
    const severityCfg = SEVERITY_CONFIG[inc.severity] || SEVERITY_CONFIG.medium;

    return (
      <div className="space-y-6 max-w-4xl">
        {/* Back button */}
        <button
          onClick={() => setSelectedIncident(null)}
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft size={15} />
          Back to Incidents
        </button>

        {/* Header */}
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <span className="font-mono text-sm text-forest-600 dark:text-forest-400 font-semibold">
                {inc.incident_number}
              </span>
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold ${statusCfg.bg} ${statusCfg.text}`}>
                {statusCfg.label}
              </span>
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold ${severityCfg.bg} ${severityCfg.text}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${severityCfg.dot}`} />
                {severityCfg.label}
              </span>
            </div>
            <h1 className="heading-display text-2xl text-foreground">{inc.title}</h1>
          </div>
          <div className="flex items-center gap-2">
            {!editing ? (
              <button
                onClick={() => setEditing(true)}
                className="px-4 py-2 bg-forest-600 hover:bg-forest-700 text-white text-sm font-semibold rounded-xl transition-colors"
              >
                Edit
              </button>
            ) : (
              <>
                <button
                  onClick={() => setEditing(false)}
                  className="px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground rounded-lg hover:bg-secondary transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpdate}
                  disabled={saving}
                  className="px-5 py-2 bg-forest-600 hover:bg-forest-700 disabled:opacity-50 text-white text-sm font-semibold rounded-xl transition-colors"
                >
                  {saving ? "Saving..." : "Save Changes"}
                </button>
              </>
            )}
            <button
              onClick={() => setPendingDeleteId(inc.id)}
              className="p-2 text-muted-foreground hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30 rounded-lg transition-colors"
              title="Delete"
            >
              <Trash2 size={16} />
            </button>
          </div>
        </div>

        {editing ? (
          /* Edit form */
          <div className="bg-card border border-border rounded-xl p-6 space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">Title *</label>
                <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                  className="w-full px-3 py-2.5 bg-background border border-border rounded-lg text-sm text-foreground" />
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">Description</label>
                <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                  rows={3} className="w-full px-3 py-2.5 bg-background border border-border rounded-lg text-sm text-foreground resize-none" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">Type</label>
                <div className="relative">
                  <select value={form.incident_type} onChange={(e) => setForm({ ...form, incident_type: e.target.value })}
                    className="w-full px-3 py-2.5 bg-background border border-border rounded-lg text-sm text-foreground appearance-none pr-8">
                    {INCIDENT_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                  <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">Severity</label>
                <div className="relative">
                  <select value={form.severity} onChange={(e) => setForm({ ...form, severity: e.target.value })}
                    className="w-full px-3 py-2.5 bg-background border border-border rounded-lg text-sm text-foreground appearance-none pr-8">
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="critical">Critical</option>
                  </select>
                  <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">Status</label>
                <div className="relative">
                  <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}
                    className="w-full px-3 py-2.5 bg-background border border-border rounded-lg text-sm text-foreground appearance-none pr-8">
                    <option value="open">Open</option>
                    <option value="investigating">Investigating</option>
                    <option value="resolved">Resolved</option>
                    <option value="closed">Closed</option>
                  </select>
                  <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">Location</label>
                <input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })}
                  className="w-full px-3 py-2.5 bg-background border border-border rounded-lg text-sm text-foreground" placeholder="e.g. Loading dock, Windrow 3" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">Date Occurred</label>
                <input type="date" value={form.date_occurred} onChange={(e) => setForm({ ...form, date_occurred: e.target.value })}
                  className="w-full px-3 py-2.5 bg-background border border-border rounded-lg text-sm text-foreground" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">Assigned To</label>
                <input value={form.assigned_to} onChange={(e) => setForm({ ...form, assigned_to: e.target.value })}
                  className="w-full px-3 py-2.5 bg-background border border-border rounded-lg text-sm text-foreground" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">Witnesses</label>
                <input value={form.witnesses} onChange={(e) => setForm({ ...form, witnesses: e.target.value })}
                  className="w-full px-3 py-2.5 bg-background border border-border rounded-lg text-sm text-foreground" placeholder="Comma-separated names" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">Equipment Involved</label>
                <input value={form.equipment_involved} onChange={(e) => setForm({ ...form, equipment_involved: e.target.value })}
                  className="w-full px-3 py-2.5 bg-background border border-border rounded-lg text-sm text-foreground" />
              </div>
              <div>
                <label className="flex items-center gap-2 cursor-pointer mt-6">
                  <input type="checkbox" checked={form.injuries_reported} onChange={(e) => setForm({ ...form, injuries_reported: e.target.checked })}
                    className="rounded border-border w-4 h-4" />
                  <span className="text-sm font-medium text-foreground">Injuries Reported</span>
                </label>
              </div>
              {form.injuries_reported && (
                <div className="md:col-span-2">
                  <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">Injury Details</label>
                  <textarea value={form.injury_details} onChange={(e) => setForm({ ...form, injury_details: e.target.value })}
                    rows={2} className="w-full px-3 py-2.5 bg-background border border-border rounded-lg text-sm text-foreground resize-none" />
                </div>
              )}
              <div className="md:col-span-2">
                <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">Root Cause</label>
                <textarea value={form.root_cause} onChange={(e) => setForm({ ...form, root_cause: e.target.value })}
                  rows={2} className="w-full px-3 py-2.5 bg-background border border-border rounded-lg text-sm text-foreground resize-none" />
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">Corrective Actions</label>
                <textarea value={form.corrective_actions} onChange={(e) => setForm({ ...form, corrective_actions: e.target.value })}
                  rows={2} className="w-full px-3 py-2.5 bg-background border border-border rounded-lg text-sm text-foreground resize-none" />
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">Preventive Actions</label>
                <textarea value={form.preventive_actions} onChange={(e) => setForm({ ...form, preventive_actions: e.target.value })}
                  rows={2} className="w-full px-3 py-2.5 bg-background border border-border rounded-lg text-sm text-foreground resize-none" />
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">Resolution Notes</label>
                <textarea value={form.resolution_notes} onChange={(e) => setForm({ ...form, resolution_notes: e.target.value })}
                  rows={2} className="w-full px-3 py-2.5 bg-background border border-border rounded-lg text-sm text-foreground resize-none" />
              </div>
            </div>
          </div>
        ) : (
          /* Read-only detail */
          <div className="space-y-4">
            {/* Info grid */}
            <div className="bg-card border border-border rounded-xl p-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-4">
                <div>
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">Type</p>
                  <p className="text-sm text-foreground">{formatTypeLabel(inc.incident_type)}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">Date Occurred</p>
                  <p className="text-sm text-foreground">{formatDateTime(inc.date_occurred)}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">Date Reported</p>
                  <p className="text-sm text-foreground">{formatDateTime(inc.date_reported)}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">Reported By</p>
                  <p className="text-sm text-foreground">{inc.reported_by || "--"}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">Location</p>
                  <p className="text-sm text-foreground">{inc.location || "--"}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">Assigned To</p>
                  <p className="text-sm text-foreground">{inc.assigned_to || "--"}</p>
                </div>
                {inc.witnesses && (
                  <div>
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">Witnesses</p>
                    <p className="text-sm text-foreground">{inc.witnesses}</p>
                  </div>
                )}
                {inc.equipment_involved && (
                  <div>
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">Equipment Involved</p>
                    <p className="text-sm text-foreground">{inc.equipment_involved}</p>
                  </div>
                )}
                <div>
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">Injuries Reported</p>
                  <p className="text-sm text-foreground">{inc.injuries_reported ? "Yes" : "No"}</p>
                </div>
                {inc.resolved_at && (
                  <div>
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">Resolved At</p>
                    <p className="text-sm text-foreground">{formatDateTime(inc.resolved_at)}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Description */}
            {inc.description && (
              <div className="bg-card border border-border rounded-xl p-6">
                <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Description</h3>
                <p className="text-sm text-foreground whitespace-pre-wrap">{inc.description}</p>
              </div>
            )}

            {/* Injuries */}
            {inc.injuries_reported && inc.injury_details && (
              <div className="bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-900 rounded-xl p-6">
                <h3 className="text-xs font-semibold text-red-700 dark:text-red-400 uppercase tracking-wide mb-2">Injury Details</h3>
                <p className="text-sm text-foreground whitespace-pre-wrap">{inc.injury_details}</p>
              </div>
            )}

            {/* Root cause & actions */}
            {(inc.root_cause || inc.corrective_actions || inc.preventive_actions || inc.resolution_notes) && (
              <div className="bg-card border border-border rounded-xl p-6 space-y-4">
                {inc.root_cause && (
                  <div>
                    <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">Root Cause</h3>
                    <p className="text-sm text-foreground whitespace-pre-wrap">{inc.root_cause}</p>
                  </div>
                )}
                {inc.corrective_actions && (
                  <div>
                    <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">Corrective Actions</h3>
                    <p className="text-sm text-foreground whitespace-pre-wrap">{inc.corrective_actions}</p>
                  </div>
                )}
                {inc.preventive_actions && (
                  <div>
                    <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">Preventive Actions</h3>
                    <p className="text-sm text-foreground whitespace-pre-wrap">{inc.preventive_actions}</p>
                  </div>
                )}
                {inc.resolution_notes && (
                  <div>
                    <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">Resolution Notes</h3>
                    <p className="text-sm text-foreground whitespace-pre-wrap">{inc.resolution_notes}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Delete modal for detail view */}
        {pendingDeleteId !== null && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-card border border-border rounded-2xl w-full max-w-md shadow-2xl">
              <div className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                    <Trash2 size={18} className="text-red-600 dark:text-red-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-foreground">Delete Incident</h3>
                    <p className="text-sm text-muted-foreground">This action cannot be undone.</p>
                  </div>
                </div>
              </div>
              <div className="flex justify-end gap-3 p-6 border-t border-border">
                <button onClick={() => setPendingDeleteId(null)}
                  className="px-4 py-2.5 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors rounded-lg hover:bg-secondary">
                  Cancel
                </button>
                <button onClick={handleDeleteSingle} disabled={deleting}
                  className="px-5 py-2.5 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white text-sm font-semibold rounded-xl transition-colors">
                  {deleting ? "Deleting..." : "Delete"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // -----------------------------------------------------------------------
  // List view
  // -----------------------------------------------------------------------

  return (
    <div className="space-y-4 max-w-7xl">
      {/* Header + stats inline */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 flex-wrap">
        <div className="flex items-baseline gap-4 flex-wrap">
          <h1 className="heading-display text-2xl text-foreground">Incident Reports</h1>
          {!loading && (
            <div className="flex items-center gap-4 text-sm">
              <span className="flex items-center gap-1.5 text-muted-foreground">
                <Shield size={14} className="text-forest-500" />
                <strong className="text-foreground tabular-nums">{stats.total}</strong> total
              </span>
              <span className="flex items-center gap-1.5 text-muted-foreground">
                <AlertTriangle size={14} className="text-red-500" />
                <strong className="text-foreground tabular-nums">{stats.open}</strong> open
              </span>
              <span className="flex items-center gap-1.5 text-muted-foreground">
                <Clock size={14} className="text-yellow-500" />
                <strong className="text-foreground tabular-nums">{stats.investigating}</strong> investigating
              </span>
              <span className="flex items-center gap-1.5 text-muted-foreground">
                <AlertTriangle size={14} className={stats.critical > 0 ? "text-red-500" : "text-muted-foreground"} />
                <strong className={`tabular-nums ${stats.critical > 0 ? "text-red-600 dark:text-red-400" : "text-foreground"}`}>{stats.critical}</strong> critical active
              </span>
            </div>
          )}
        </div>
        <button
          onClick={() => { setForm(emptyForm); setShowCreateModal(true); }}
          className="inline-flex items-center gap-2 px-4 py-2 bg-forest-600 hover:bg-forest-700 text-white text-sm font-semibold rounded-lg transition-colors shadow-sm"
        >
          <Plus size={16} strokeWidth={2.5} />
          New Incident
        </button>
      </div>

      {/* Filters row */}
      <div className="bg-card border border-border rounded-xl p-3 space-y-2.5">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          {/* Search */}
          <label className="flex items-center gap-2 px-3 bg-background border border-border rounded-lg">
            <Search size={15} className="text-muted-foreground shrink-0" />
            <input value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search incident #, title, reporter..."
              className="w-full py-2 text-sm bg-transparent outline-none text-foreground placeholder:text-muted-foreground/60" />
            {search && (
              <button onClick={() => setSearch("")} className="text-muted-foreground hover:text-foreground">
                <X size={14} />
              </button>
            )}
          </label>

          {/* Severity filter */}
          <div className="relative">
            <select value={severityFilter} onChange={(e) => setSeverityFilter(e.target.value as SeverityFilter)}
              className="w-full px-3 py-2.5 bg-background border border-border rounded-lg text-sm text-foreground appearance-none pr-8">
              <option value="all">All Severities</option>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="critical">Critical</option>
            </select>
            <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
          </div>

          {/* Status filter */}
          <div className="relative">
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
              className="w-full px-3 py-2.5 bg-background border border-border rounded-lg text-sm text-foreground appearance-none pr-8">
              <option value="all">All Statuses</option>
              <option value="open">Open</option>
              <option value="investigating">Investigating</option>
              <option value="resolved">Resolved</option>
              <option value="closed">Closed</option>
            </select>
            <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
          </div>
        </div>

        {/* Status pill tabs */}
        <div className="flex gap-1.5 flex-wrap">
          {([
            { key: "all", label: "All" },
            { key: "open", label: "Open" },
            { key: "investigating", label: "Investigating" },
            { key: "resolved", label: "Resolved" },
            { key: "closed", label: "Closed" },
          ] as const).map((tab) => (
            <button key={tab.key} onClick={() => setStatusFilter(tab.key)}
              className={`px-3.5 py-1.5 text-xs font-semibold rounded-lg transition-colors ${
                statusFilter === tab.key
                  ? "bg-forest-600 text-white shadow-sm"
                  : "bg-secondary text-muted-foreground hover:text-foreground"
              }`}>
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Bulk actions */}
      {selected.size > 0 && (
        <div className="flex items-center gap-3 bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-900 rounded-xl px-4 py-3">
          <span className="text-sm font-medium text-red-700 dark:text-red-400">
            {selected.size} selected
          </span>
          <button onClick={() => setShowDeleteConfirm(true)} disabled={deleting}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white text-xs font-semibold rounded-lg transition-colors">
            <Trash2 size={13} /> Delete Selected
          </button>
          <button onClick={() => setSelected(new Set())}
            className="text-xs text-muted-foreground hover:text-foreground ml-auto">
            Clear selection
          </button>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-900 rounded-xl p-4 flex items-center gap-3">
          <AlertTriangle size={16} className="text-red-600 dark:text-red-400 shrink-0" />
          <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
          <button onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-600">
            <X size={14} />
          </button>
        </div>
      )}

      {/* Table */}
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-muted-foreground">Loading incidents...</div>
        ) : filtered.length === 0 ? (
          <div className="p-16 text-center">
            <Shield size={40} className="mx-auto text-muted-foreground/30 mb-3" />
            <p className="text-sm text-muted-foreground">
              {search || statusFilter !== "all" || severityFilter !== "all"
                ? "No incidents match your filters."
                : "No incidents reported yet."}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="px-3 py-2 w-10">
                    <input type="checkbox" checked={selected.size === filtered.length && filtered.length > 0}
                      onChange={toggleSelectAll} className="rounded border-border" />
                  </th>
                  <th className="px-3 py-2">Incident #</th>
                  <th className="px-3 py-2">Title</th>
                  <th className="px-3 py-2">Type</th>
                  <th className="px-3 py-2">Severity</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">Date</th>
                  <th className="px-3 py-2">Reported By</th>
                  <th className="px-3 py-2 w-[100px] text-right" aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {filtered.map((inc) => {
                  const statusCfg = STATUS_CONFIG[inc.status] || STATUS_CONFIG.open;
                  const severityCfg = SEVERITY_CONFIG[inc.severity] || SEVERITY_CONFIG.medium;
                  const isSelected = selected.has(inc.id);

                  return (
                    <tr key={inc.id} onClick={() => openDetail(inc.id)}
                      className={`border-b border-border/60 last:border-0 transition-colors cursor-pointer ${
                        isSelected ? "bg-forest-50/50 dark:bg-forest-950/20" : "hover:bg-muted/30"
                      }`}>
                      <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                        <input type="checkbox" checked={isSelected} onChange={() => toggleSelect(inc.id)}
                          className="rounded border-border" />
                      </td>
                      <td className="px-3 py-2">
                        <span className="font-semibold text-forest-600 dark:text-forest-400 font-mono text-xs">
                          {inc.incident_number}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-foreground font-medium max-w-[220px] truncate">
                        {inc.title}
                      </td>
                      <td className="px-3 py-2 text-muted-foreground whitespace-nowrap text-xs">
                        {formatTypeLabel(inc.incident_type)}
                      </td>
                      <td className="px-3 py-2">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold ${severityCfg.bg} ${severityCfg.text}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${severityCfg.dot}`} />
                          {severityCfg.label}
                        </span>
                      </td>
                      <td className="px-3 py-2">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold ${statusCfg.bg} ${statusCfg.text}`}>
                          {statusCfg.label}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">
                        {formatDate(inc.date_occurred || inc.date_reported)}
                      </td>
                      <td className="px-3 py-2 text-muted-foreground max-w-[140px] truncate">
                        {inc.reported_by || "--"}
                      </td>
                      <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-1">
                          <button onClick={() => openDetail(inc.id)} title="View"
                            className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-secondary rounded-lg transition-colors">
                            <Eye size={15} />
                          </button>
                          <button onClick={() => setPendingDeleteId(inc.id)} title="Delete"
                            className="p-1.5 text-muted-foreground hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30 rounded-lg transition-colors">
                            <Trash2 size={15} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-card border border-border rounded-2xl w-full max-w-2xl shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-border">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-foreground">New Incident Report</h2>
                <button onClick={() => setShowCreateModal(false)} className="p-1.5 text-muted-foreground hover:text-foreground rounded-lg hover:bg-secondary">
                  <X size={18} />
                </button>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">Title *</label>
                <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="Brief description of the incident"
                  className="w-full px-3 py-2.5 bg-background border border-border rounded-lg text-sm text-foreground" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">Description</label>
                <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                  rows={3} placeholder="Detailed description of what happened..."
                  className="w-full px-3 py-2.5 bg-background border border-border rounded-lg text-sm text-foreground resize-none" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">Incident Type</label>
                  <div className="relative">
                    <select value={form.incident_type} onChange={(e) => setForm({ ...form, incident_type: e.target.value })}
                      className="w-full px-3 py-2.5 bg-background border border-border rounded-lg text-sm text-foreground appearance-none pr-8">
                      {INCIDENT_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                    <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">Severity</label>
                  <div className="relative">
                    <select value={form.severity} onChange={(e) => setForm({ ...form, severity: e.target.value })}
                      className="w-full px-3 py-2.5 bg-background border border-border rounded-lg text-sm text-foreground appearance-none pr-8">
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="critical">Critical</option>
                    </select>
                    <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">Location</label>
                  <input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })}
                    placeholder="e.g. Loading dock, Windrow 3"
                    className="w-full px-3 py-2.5 bg-background border border-border rounded-lg text-sm text-foreground" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">Date Occurred</label>
                  <input type="date" value={form.date_occurred} onChange={(e) => setForm({ ...form, date_occurred: e.target.value })}
                    className="w-full px-3 py-2.5 bg-background border border-border rounded-lg text-sm text-foreground" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">Witnesses</label>
                <input value={form.witnesses} onChange={(e) => setForm({ ...form, witnesses: e.target.value })}
                  placeholder="Comma-separated names"
                  className="w-full px-3 py-2.5 bg-background border border-border rounded-lg text-sm text-foreground" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">Equipment Involved</label>
                <input value={form.equipment_involved} onChange={(e) => setForm({ ...form, equipment_involved: e.target.value })}
                  placeholder="e.g. Rotomixer, Loader #2"
                  className="w-full px-3 py-2.5 bg-background border border-border rounded-lg text-sm text-foreground" />
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.injuries_reported} onChange={(e) => setForm({ ...form, injuries_reported: e.target.checked })}
                  className="rounded border-border w-4 h-4" />
                <span className="text-sm font-medium text-foreground">Injuries Reported</span>
              </label>
              {form.injuries_reported && (
                <div>
                  <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">Injury Details</label>
                  <textarea value={form.injury_details} onChange={(e) => setForm({ ...form, injury_details: e.target.value })}
                    rows={2} placeholder="Describe the injuries..."
                    className="w-full px-3 py-2.5 bg-background border border-border rounded-lg text-sm text-foreground resize-none" />
                </div>
              )}
            </div>
            <div className="flex justify-end gap-3 p-6 border-t border-border">
              <button onClick={() => setShowCreateModal(false)}
                className="px-4 py-2.5 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors rounded-lg hover:bg-secondary">
                Cancel
              </button>
              <button onClick={handleCreate} disabled={creating || !form.title.trim()}
                className="px-5 py-2.5 bg-forest-600 hover:bg-forest-700 disabled:opacity-50 text-white text-sm font-semibold rounded-xl transition-colors">
                {creating ? "Creating..." : "Create Incident"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk delete confirmation modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-card border border-border rounded-2xl w-full max-w-md shadow-2xl">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                  <Trash2 size={18} className="text-red-600 dark:text-red-400" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-foreground">Delete Incidents</h3>
                  <p className="text-sm text-muted-foreground">This action cannot be undone.</p>
                </div>
              </div>
              <p className="text-sm text-foreground">
                Are you sure you want to permanently delete{" "}
                <strong>{selected.size}</strong> incident{selected.size !== 1 ? "s" : ""}?
              </p>
            </div>
            <div className="flex justify-end gap-3 p-6 border-t border-border">
              <button onClick={() => setShowDeleteConfirm(false)}
                className="px-4 py-2.5 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors rounded-lg hover:bg-secondary">
                Cancel
              </button>
              <button onClick={handleBulkDelete} disabled={deleting}
                className="px-5 py-2.5 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white text-sm font-semibold rounded-xl transition-colors">
                {deleting ? "Deleting..." : `Delete ${selected.size} Incident${selected.size !== 1 ? "s" : ""}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Single delete confirmation modal */}
      {pendingDeleteId !== null && !selectedIncident && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-card border border-border rounded-2xl w-full max-w-md shadow-2xl">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                  <Trash2 size={18} className="text-red-600 dark:text-red-400" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-foreground">Delete Incident</h3>
                  <p className="text-sm text-muted-foreground">This action cannot be undone.</p>
                </div>
              </div>
              <p className="text-sm text-foreground">
                Are you sure you want to permanently delete incident{" "}
                <strong>{incidents.find((i) => i.id === pendingDeleteId)?.incident_number || `#${pendingDeleteId}`}</strong>?
              </p>
            </div>
            <div className="flex justify-end gap-3 p-6 border-t border-border">
              <button onClick={() => setPendingDeleteId(null)}
                className="px-4 py-2.5 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors rounded-lg hover:bg-secondary">
                Cancel
              </button>
              <button onClick={handleDeleteSingle} disabled={deleting}
                className="px-5 py-2.5 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white text-sm font-semibold rounded-xl transition-colors">
                {deleting ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Detail loading */}
      {detailLoading && (
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-50 flex items-center justify-center">
          <div className="bg-card border border-border rounded-xl px-8 py-6 shadow-2xl">
            <p className="text-sm text-muted-foreground">Loading incident...</p>
          </div>
        </div>
      )}
    </div>
  );
}
